/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {useContext} from 'react'
import {MultiSiteContext} from '../contexts'

/**
 * Custom React hook to get the multi-site values: the site, the locale and the function used to
 * build URLs following the App configuration.
 *
 * `buildUrl` keeps its identity across renders and components and caches urls built for the
 * current site and locale, so its result is used directly without `useMemo`.
 * @returns {{site, locale, buildUrl: (function(string, string=, string=): string)}}
 * @example
 * const {buildUrl} = useMultiSite()
 *
 * return <Link to={buildUrl(HOME_HREF)} />
 */
const useMultiSite = () => {
    const context = useContext(MultiSiteContext)
    if (context === undefined) {
        throw new Error('useMultiSite must be used within MultiSiteProvider')
    }
    const {site, locale, buildUrl} = context
    return {site, locale, buildUrl}
}

export default useMultiSite
